import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface ZoneInfo {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  available: boolean;
}

interface StepZoneProps {
  zones: ZoneInfo[];
  loading: boolean;
  selectedZone: string | null;
  onSelect: (zoneId: string | null) => void;
  onAutoAdvance: () => void;
}

const StepZone: React.FC<StepZoneProps> = ({ zones, loading, selectedZone, onSelect, onAutoAdvance }) => {
  const handleSelect = (zoneId: string | null) => {
    onSelect(zoneId);
    setTimeout(() => onAutoAdvance(), 400);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-3 py-2 px-1"
    >
      <div className="flex items-center gap-2 text-muted-foreground justify-center">
        <MapPin className="h-5 w-5" />
        <span className="text-sm font-medium">Où souhaitez-vous être installé ?</span>
      </div>

      {/* No preference option */}
      <Card
        className={`cursor-pointer transition-all hover:shadow-md ${selectedZone === null ? 'ring-2 ring-primary border-primary' : ''}`}
        onClick={() => handleSelect(null)}
      >
        <CardContent className="p-3 flex items-center gap-3">
          <span className="text-lg">✨</span>
          <div>
            <p className="font-medium text-sm">Pas de préférence</p>
            <p className="text-xs text-muted-foreground">Le meilleur emplacement disponible</p>
          </div>
        </CardContent>
      </Card>

      {/* Zone cards */}
      {zones.map(zone => (
        <Card
          key={zone.id}
          className={`transition-all ${zone.available ? 'cursor-pointer hover:shadow-md' : 'opacity-50 cursor-not-allowed'} ${selectedZone === zone.id ? 'ring-2 ring-primary border-primary' : ''}`}
          onClick={() => zone.available && handleSelect(zone.id)}
        >
          <CardContent className="p-3 flex items-center gap-3">
            <span className="text-lg">{zone.icon || '📍'}</span>
            <div className="flex-1">
              <p className="font-medium text-sm">{zone.name}</p>
              {zone.description && <p className="text-xs text-muted-foreground italic">{zone.description}</p>}
            </div>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${zone.available ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
              {zone.available ? 'Disponible' : 'Complet'}
            </span>
          </CardContent>
        </Card>
      ))}
    </motion.div>
  );
};

export default StepZone;
